import { Head, useForm } from "@inertiajs/react"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { SiriOrb } from "@/components/ui/siri-orb"
import PartnerSettingsLayout from "@/layouts/partner/settings-layout"
import PartnerLayout from "@/layouts/partner-layout"
import { ASSISTANT_THEMES, assistantTheme } from "@/lib/assistant"
import { cn } from "@/lib/utils"
import { partnerSettingsIdentityPath } from "@/routes"

interface PartnerIdentity {
  uid: string
  // The raw column value — null when the partner hasn't named the
  // assistant yet (clients then see "AI Assistant").
  assistant_name: string | null
  assistant_color: string
}

// IMP-455: the partner names the branded assistant and picks its color, once,
// for every client account under the partner.
export default function IdentitySettings({
  identity,
}: {
  identity: PartnerIdentity
}) {
  const { data, setData, patch, processing, errors } = useForm({
    partner: {
      assistant_name: identity.assistant_name ?? "",
      assistant_color: identity.assistant_color,
    },
  })

  const orb = assistantTheme(data.partner.assistant_color).colors

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(partnerSettingsIdentityPath())
  }

  return (
    <PartnerLayout>
      <Head title="Settings — Identity" />
      <PartnerSettingsLayout>
        <form onSubmit={handleSubmit}>
          <div className="space-y-8">
            <div>
              <h2 className="text-sm font-semibold">Identity</h2>
              <p className="text-muted-foreground text-sm">
                The name and color of the AI assistant your clients see across
                every account.
              </p>
            </div>

            <div className="grid gap-10 md:grid-cols-[1fr_260px]">
              <div className="space-y-8">
                <div className="space-y-2">
                  <Label htmlFor="assistant-name">Assistant name</Label>
                  <Input
                    id="assistant-name"
                    value={data.partner.assistant_name}
                    placeholder="AI Assistant"
                    onChange={(e) =>
                      setData("partner", {
                        ...data.partner,
                        assistant_name: e.target.value,
                      })
                    }
                    className="max-w-xs"
                  />
                  <InputError
                    message={(errors as Record<string, string>)?.assistant_name}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Color</Label>
                  <div className="flex flex-wrap items-center gap-2.5 pt-1">
                    {ASSISTANT_THEMES.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() =>
                          setData("partner", {
                            ...data.partner,
                            assistant_color: t.id,
                          })
                        }
                        aria-label={t.id}
                        className={cn(
                          "size-8 rounded-full border-2 transition-transform",
                          data.partner.assistant_color === t.id
                            ? "border-foreground scale-110"
                            : "border-transparent",
                        )}
                        style={{ backgroundColor: t.swatch }}
                      />
                    ))}
                  </div>
                  <InputError
                    message={
                      (errors as Record<string, string>)?.assistant_color
                    }
                  />
                </div>

                <Button className="rounded-sm" loading={processing}>
                  Save changes
                </Button>
              </div>

              {/* Live preview */}
              <div className="bg-muted/40 flex flex-col items-center justify-center gap-4 rounded-xl border p-8">
                <SiriOrb size={120} colors={orb} className="drop-shadow-xl" />
                <p className="text-sm font-semibold">
                  {data.partner.assistant_name || "AI Assistant"}
                </p>
              </div>
            </div>
          </div>
        </form>
      </PartnerSettingsLayout>
    </PartnerLayout>
  )
}
